/**
 * The change register (P421).
 *
 * A significant change -- a new product, an acquisition, a new market, a
 * technology system, a process, a vendor, a facility, a regulatory
 * change -- is raised over what it affects, assessed in words by a named
 * person, and closed. Before anybody assesses one, the page says what
 * assessing will do: which admitted services it reaches, whose review
 * comes forward to when, and how many findings it raises. A routine
 * change is recorded and does none of that.
 */
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { refusalMessage } from "shared/api/refusalMessage";
import { holdsRealmRole, holdsSeat, useMe } from "shared/api/useMe";
import type { AffectedKind, ChangeEventRow, ChangeKind, MeOut } from "shared/api/types";
import { Explainer } from "shared/components/Explainer";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { StatusPill, type PillTone } from "shared/components/StatusPill";
import { formatDate } from "shared/time/formatDate";
import { toldSentence } from "shared/changes/toldSentence";
import { useToast } from "shared/toast/ToastProvider";
import {
  useAffectable,
  useAssessChange,
  useChangePreview,
  useChanges,
  useCloseChange,
  useEditChange,
  useRaiseChange,
  useWatchedKinds,
} from "twin/api/change_hooks";
import { useIBSStory } from "twin/api/hooks";
import { isInternalTool } from "twin/ibs/internalTools";

export const CHANGE_KINDS: ChangeKind[] = [
  "new_product",
  "acquisition",
  "new_market",
  "technology_system",
  "process_change",
  "vendor_change",
  "facility_change",
  "regulatory_rebase",
  "other",
];

const AFFECTED_KINDS: AffectedKind[] = [
  "ibs_service",
  "service",
  "vendor",
  "configuration_item",
  "facility",
  "recovery_team",
  "regime",
];

const BTN =
  "rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-1 text-xs text-brand-brassDark hover:bg-brand-brass/10 disabled:opacity-50";
const PRIMARY =
  "rounded bg-brand-brass px-2 py-1 text-xs text-brand-navy hover:bg-brand-brassDark disabled:opacity-50";

/** Who raises and assesses a change: the server's own seat list. */
export function mayRaiseChange(me: MeOut | undefined): boolean {
  if (!me) return false;
  return (
    me.is_admin ||
    holdsSeat(me, "preparer") ||
    holdsSeat(me, "approver") ||
    holdsRealmRole(me, "head_of_opsres")
  );
}

const STATUS_TONE: Record<ChangeEventRow["status"], PillTone> = {
  open: "amber",
  assessed: "green",
  closed: "neutral",
};

/** Today as YYYY-MM-DD in the reader's own day, to compare a due date. */
function todayIso(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

interface Picked {
  entity_kind: AffectedKind;
  entity_id: string;
  name: string;
}

export function ChangeRegisterView() {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const [params, setParams] = useSearchParams();
  /* Round 19 G40 -- the register filtered from a service's chip, or to
     the services the reader owns. */
  const ibsFilter = params.get("ibs");
  const mineFilter = params.get("mine") === "1";
  const changes = useChanges({ ibs: mineFilter ? null : ibsFilter, mine: mineFilter });
  const watched = useWatchedKinds();
  const filteredStory = useIBSStory(ibsFilter && !mineFilter ? ibsFilter : undefined);
  const [open, setOpen] = useState<string | null>(params.get("change"));
  const [raising, setRaising] = useState(false);
  const may = mayRaiseChange(me.data);
  const rows = Array.isArray(changes.data) ? changes.data : [];

  return (
    <section data-testid="change-register-view" className="flex flex-col gap-4">
      <header>
        <p className="text-[11px] uppercase tracking-wide text-slate-500">
          {t("modules.twin.changes.kicker")}
        </p>
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="journey"
            size={22}
            className="me-2 inline-block align-[-0.12em] text-brand-brassDark"
          />
          {t("modules.twin.changes.heading")}
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-600">
          {t("modules.twin.changes.standfirst")}
        </p>
      </header>
      {/* Round 19 G39 -- the register holds both kinds; each is defined. */}
      <Explainer termKey="significant_change" className="max-w-3xl" />
      <Explainer termKey="routine_change" className="max-w-3xl" />
      {(ibsFilter || mineFilter || watched.data?.owns_services) && (
        <div
          data-testid="change-register-filter"
          className="flex flex-wrap items-center gap-2 text-xs text-slate-700"
        >
          {mineFilter ? (
            <span>{t("modules.twin.changes.filteredMine")}</span>
          ) : ibsFilter ? (
            <span>
              {t("modules.twin.changes.filteredTo", {
                name: filteredStory.data?.name ?? "…",
              })}
            </span>
          ) : null}{" "}
          {!mineFilter && watched.data?.owns_services && (
            <button
              type="button"
              data-testid="change-filter-mine"
              className="text-brand-brassDark hover:underline"
              onClick={() => setParams({ mine: "1" })}
            >
              {t("modules.twin.changes.filterMine")}
            </button>
          )}{" "}
          {(ibsFilter || mineFilter) && (
            <button
              type="button"
              data-testid="change-filter-clear"
              className="text-brand-brassDark hover:underline"
              onClick={() => setParams({})}
            >
              {t("modules.twin.changes.filterAll")}
            </button>
          )}
        </div>
      )}
      {may && (
        <div>
          <button
            type="button"
            data-testid="change-raise-toggle"
            className={BTN}
            onClick={() => setRaising((v) => !v)}
          >
            {t("modules.twin.changes.raise")}
          </button>
        </div>
      )}
      {raising && may && (
        <RaiseForm
          onDone={(id) => {
            setRaising(false);
            setOpen(id);
          }}
          onCancel={() => setRaising(false)}
        />
      )}
      {changes.isLoading ? (
        <p className="text-sm text-slate-500">{t("modules.twin.changes.loading")}</p>
      ) : rows.length === 0 ? (
        <p data-testid="change-register-empty" className="text-sm text-slate-600">
          {t("modules.twin.changes.empty")}
        </p>
      ) : (
        <ul data-testid="change-register" className="flex flex-col gap-2">
          {rows.map((c) => (
            <li
              key={c.id}
              data-testid={`change-row-${c.id}`}
              className="rounded border border-paper-2 bg-white px-3 py-2 text-sm"
            >
              <button
                type="button"
                className="flex w-full flex-wrap items-baseline gap-x-3 text-start"
                aria-expanded={open === c.id}
                onClick={() => setOpen(open === c.id ? null : c.id)}
              >
                <span className="font-medium text-slate-900" dir="auto">
                  {c.title}
                </span>
                <span className="text-xs text-slate-600">
                  {t(`modules.twin.changes.kind.${c.kind}`)}
                </span>
                <StatusPill
                  tone={c.significance === "significant" ? "amber" : "neutral"}
                >
                  {t(`modules.twin.changes.significance.${c.significance}`)}
                </StatusPill>
                <StatusPill tone={STATUS_TONE[c.status]}>
                  {t(`modules.twin.changes.status.${c.status}`)}
                </StatusPill>
                <span className="text-xs text-slate-500">
                  {t("modules.twin.changes.raisedOn", {
                    date: formatDate(c.raised_at, i18n.language),
                    who: c.raised_by_name ?? t("modules.twin.changes.someone"),
                  })}
                </span>
              </button>
              {open === c.id && <ChangeDetail change={c} may={may} />}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function AffectedPicker({
  picked,
  onChange,
}: {
  picked: Picked[];
  onChange: (next: Picked[]) => void;
}) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<AffectedKind>("service");
  const [q, setQ] = useState("");
  /* Round 19 G39 -- the picker offered Canteen booking and the
     internal wiki beside the services a change reaches; Raise a
     function holds them behind a fold, and so does this. */
  const [showInternal, setShowInternal] = useState(false);
  const options = useAffectable(kind, q, true);
  const chosen = new Set(picked.map((p) => `${p.entity_kind}:${p.entity_id}`));
  const all = (Array.isArray(options.data) ? options.data : []).filter(
    (o) => !chosen.has(`${o.entity_kind}:${o.entity_id}`),
  );
  const internal = all.filter((o) => isInternalTool(o));
  const list = showInternal ? all : all.filter((o) => !isInternalTool(o));
  return (
    <div data-testid="change-affected-picker" className="flex flex-col gap-1">
      <span className="text-xs font-medium text-slate-700">
        {t("modules.twin.changes.affected")}
      </span>
      {picked.length > 0 && (
        <ul className="flex flex-wrap gap-1">
          {picked.map((p) => (
            <li
              key={`${p.entity_kind}:${p.entity_id}`}
              data-testid="change-affected-chosen"
              className="rounded border border-slate-300 px-1.5 py-0.5 text-xs"
            >
              <bdi>{p.name}</bdi>{" "}
              <span className="text-slate-500">
                ({t(`modules.twin.changes.affectedKind.${p.entity_kind}`)})
              </span>{" "}
              <button
                type="button"
                aria-label={t("modules.twin.changes.remove", { name: p.name })}
                className="text-slate-500 hover:text-rose-700"
                onClick={() =>
                  onChange(
                    picked.filter(
                      (x) =>
                        !(
                          x.entity_kind === p.entity_kind && x.entity_id === p.entity_id
                        ),
                    ),
                  )
                }
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <select
          data-testid="change-affected-kind"
          aria-label={t("modules.twin.changes.affectedKindLabel")}
          value={kind}
          onChange={(e) => setKind(e.target.value as AffectedKind)}
          className="rounded border border-paper-2 bg-paper px-2 py-1 text-xs"
        >
          {AFFECTED_KINDS.map((k) => (
            <option key={k} value={k}>
              {t(`modules.twin.changes.affectedKind.${k}`)}
            </option>
          ))}
        </select>
        <input
          data-testid="change-affected-search"
          aria-label={t("modules.twin.changes.search")}
          placeholder={t("modules.twin.changes.search")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="rounded border border-paper-2 px-2 py-1 text-xs"
        />
      </div>
      {internal.length > 0 && (
        <button
          type="button"
          data-testid="change-affected-internal-toggle"
          className="self-start text-xs text-slate-600 hover:underline"
          onClick={() => setShowInternal((v) => !v)}
        >
          {showInternal
            ? t("modules.twin.changes.hideInternal")
            : t("modules.twin.changes.showInternal", { count: internal.length })}
        </button>
      )}
      <ul className="max-h-40 overflow-y-auto">
        {list.map((o) => (
          <li key={`${o.entity_kind}:${o.entity_id}`}>
            <button
              type="button"
              data-testid="change-affected-option"
              className="text-xs text-brand-brassDark hover:underline"
              onClick={() => onChange([...picked, o])}
            >
              <bdi>{o.name}</bdi>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RaiseForm({
  onDone,
  onCancel,
}: {
  onDone: (id: string) => void;
  /** Round 19 G39 -- the form had no Cancel. */
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const raise = useRaiseChange();
  const watched = useWatchedKinds();
  const [kind, setKind] = useState<ChangeKind>("technology_system");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [effective, setEffective] = useState("");
  /* Round 19 G37 -- no default: a preparer who did not read the radio
     raised a significant change, and assessing it moved reviews and
     raised findings on services other people own. Raise waits for a
     choice. */
  const [significance, setSignificance] = useState<"significant" | "routine" | null>(
    null,
  );
  const [picked, setPicked] = useState<Picked[]>([]);
  return (
    <form
      data-testid="change-raise-form"
      className="flex flex-col gap-2 rounded border border-paper-2 bg-white p-3 text-sm"
      onSubmit={async (e) => {
        e.preventDefault();
        if (significance === null) return;
        try {
          const row = await raise.mutateAsync({
            kind,
            title: title.trim(),
            description: description.trim() || null,
            effective_from: effective || null,
            significance,
            affected: picked.map((p) => ({
              entity_kind: p.entity_kind,
              entity_id: p.entity_id,
            })),
          });
          toast.push({
            tone: "success",
            message: [t("modules.twin.changes.raised"), toldSentence(t, row.told)]
              .filter(Boolean)
              .join(" "),
          });
          onDone(row.id);
        } catch (err) {
          toast.push({ tone: "warning", message: refusalMessage(t, err) });
        }
      }}
    >
      <label className="flex flex-col gap-1 text-xs font-medium text-slate-700">
        {t("modules.twin.changes.kindLabel")}
        <select
          data-testid="change-kind"
          value={kind}
          onChange={(e) => setKind(e.target.value as ChangeKind)}
          className="rounded border border-paper-2 bg-paper px-2 py-1 text-xs font-normal"
        >
          {CHANGE_KINDS.map((k) => (
            <option key={k} value={k}>
              {t(`modules.twin.changes.kind.${k}`)}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-xs font-medium text-slate-700">
        {t("modules.twin.changes.titleLabel")}
        <input
          data-testid="change-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="rounded border border-paper-2 px-2 py-1 text-sm font-normal"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs font-medium text-slate-700">
        {t("modules.twin.changes.descriptionLabel")}
        <textarea
          data-testid="change-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          className="rounded border border-paper-2 px-2 py-1 text-sm font-normal"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs font-medium text-slate-700">
        {t("modules.twin.changes.effectiveLabel")}
        <input
          type="date"
          data-testid="change-effective"
          value={effective}
          onChange={(e) => setEffective(e.target.value)}
          className="w-44 rounded border border-paper-2 px-2 py-1 text-sm font-normal"
        />
      </label>
      <fieldset className="flex flex-col gap-1 text-xs">
        <legend className="font-medium text-slate-700">
          {t("modules.twin.changes.significanceLabel")}
        </legend>
        {(["significant", "routine"] as const).map((s) => (
          <label key={s} className="flex items-start gap-1.5">
            <input
              type="radio"
              name="change-significance"
              data-testid={`change-significance-${s}`}
              checked={significance === s}
              onChange={() => setSignificance(s)}
            />
            <span>{t(`modules.twin.changes.significanceHelp.${s}`)}</span>
          </label>
        ))}
      </fieldset>
      {significance !== null && (
        <p data-testid="change-who-is-told" className="text-xs text-slate-600">
          {significance === "routine"
            ? t("modules.twin.changes.tellsNobody")
            : (watched.data?.kinds ?? []).includes(kind)
              ? t("modules.twin.changes.tellsApprovers", {
                  kind: t(`modules.twin.changes.kindWord.${kind}`),
                })
              : t("modules.twin.changes.tellsNobodySignificant")}
        </p>
      )}
      <AffectedPicker picked={picked} onChange={setPicked} />
      <div>
        <button
          type="submit"
          data-testid="change-raise-submit"
          disabled={!title.trim() || significance === null || raise.isPending}
          className={PRIMARY}
        >
          {t("modules.twin.changes.raiseSubmit")}
        </button>{" "}
        {onCancel && (
          <button
            type="button"
            data-testid="change-raise-cancel"
            onClick={onCancel}
            className="ms-2 text-xs text-slate-600 hover:underline"
          >
            {t("modules.twin.changes.raiseCancel")}
          </button>
        )}
        {significance === null && (
          <p
            data-testid="change-significance-needed"
            className="mt-1 text-xs text-slate-500"
          >
            {t("modules.twin.changes.significanceNeeded")}
          </p>
        )}
      </div>
    </form>
  );
}

function ChangeDetail({ change, may }: { change: ChangeEventRow; may: boolean }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const toast = useToast();
  const isOpen = change.status === "open";
  const significant = change.significance === "significant";
  const preview = useChangePreview(change.id, isOpen && significant);
  const assess = useAssessChange(change.id);
  const close = useCloseChange(change.id);
  const edit = useEditChange(change.id);
  const [assessment, setAssessment] = useState("");
  const [editing, setEditing] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const [picked, setPicked] = useState<Picked[]>(
    change.affected.map((a) => ({
      entity_kind: a.entity_kind,
      entity_id: a.entity_id,
      name: a.name ?? a.entity_id,
    })),
  );
  const p = preview.data;

  return (
    <div data-testid="change-detail" className="mt-2 flex flex-col gap-2 border-t pt-2">
      {change.description && (
        <p className="whitespace-pre-wrap text-xs text-slate-700" dir="auto">
          {change.description}
        </p>
      )}
      {change.effective_from && (
        <p className="text-xs text-slate-600">
          {t("modules.twin.changes.effectiveOn", {
            date: formatDate(change.effective_from, lang),
          })}
        </p>
      )}
      <div className="text-xs text-slate-700">
        <span className="font-medium">{t("modules.twin.changes.affected")}: </span>
        {change.affected.length === 0 ? (
          <span data-testid="change-affected-none" className="text-amber-900">
            {t("modules.twin.changes.affectedNone")}
          </span>
        ) : (
          <span data-testid="change-affected-list">
            {change.affected.map((a, i) => (
              <span key={`${a.entity_kind}:${a.entity_id}`}>
                {i > 0 && ", "}
                <bdi>{a.name ?? t("modules.twin.changes.gone")}</bdi>{" "}
                <span className="text-slate-500">
                  ({t(`modules.twin.changes.affectedKind.${a.entity_kind}`)})
                </span>
              </span>
            ))}
          </span>
        )}
        {isOpen && may && !editing && (
          <button
            type="button"
            data-testid="change-edit-affected"
            className="ms-2 text-brand-brassDark hover:underline"
            onClick={() => setEditing(true)}
          >
            {t("modules.twin.changes.editAffected")}
          </button>
        )}
      </div>
      {editing && (
        <div className="rounded border border-paper-2 p-2">
          <AffectedPicker picked={picked} onChange={setPicked} />
          <button
            type="button"
            data-testid="change-save-affected"
            className={`${PRIMARY} mt-1`}
            disabled={edit.isPending}
            onClick={async () => {
              try {
                await edit.mutateAsync({
                  affected: picked.map((x) => ({
                    entity_kind: x.entity_kind,
                    entity_id: x.entity_id,
                  })),
                });
                setEditing(false);
              } catch (err) {
                toast.push({ tone: "warning", message: refusalMessage(t, err) });
              }
            }}
          >
            {t("modules.twin.changes.saveAffected")}
          </button>
        </div>
      )}

      {isOpen && !significant && (
        <p data-testid="change-routine-note" className="text-xs text-slate-600">
          {t("modules.twin.changes.routineNote")}
        </p>
      )}
      {isOpen && significant && p && (
        <div
          data-testid="change-preview"
          className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-950"
        >
          <p className="font-medium">
            {p.reached.length === 0
              ? t("modules.twin.changes.previewNone")
              : [
                  t("modules.twin.changes.previewReach", { count: p.reached.length }),
                  p.reviews_pulled_forward > 0
                    ? t("modules.twin.changes.previewReviews", {
                        count: p.reviews_pulled_forward,
                        days: p.review_days,
                      })
                    : t("modules.twin.changes.previewReviewsNone"),
                  p.findings_raised > 0
                    ? t("modules.twin.changes.previewFindings", {
                        count: p.findings_raised,
                      })
                    : t("modules.twin.changes.previewFindingsNone"),
                ].join(" ")}
          </p>
          {p.reached.length > 0 && (
            <ul className="mt-1 space-y-0.5">
              {p.reached.map((r) => (
                <li key={r.ibs_service_id} data-testid="change-preview-row">
                  <Link
                    to={`/twin/ibs/${r.ibs_service_id}`}
                    className="text-brand-brassDark hover:underline"
                  >
                    <bdi>{r.name}</bdi>
                  </Link>
                  {": "}
                  {r.new_due
                    ? t("modules.twin.changes.reviewMoves", {
                        from: r.current_due
                          ? formatDate(r.current_due, lang)
                          : t("modules.twin.changes.notScheduled"),
                        to: formatDate(r.new_due, lang),
                      })
                    : r.current_due && r.current_due < todayIso()
                      ? t("modules.twin.changes.reviewOverdue", {
                          date: formatDate(r.current_due, lang),
                        })
                      : t("modules.twin.changes.reviewStays", {
                          date: r.current_due ? formatDate(r.current_due, lang) : "",
                        })}
                </li>
              ))}
            </ul>
          )}
          {p.vendors_named > 0 && (
            <p className="mt-1">
              {t("modules.twin.changes.previewVendors", { count: p.vendors_named })}
            </p>
          )}
        </div>
      )}
      {isOpen && may && (
        <form
          className="flex flex-col gap-1"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await assess.mutateAsync(assessment.trim());
              toast.push({
                tone: "success",
                message: significant
                  ? t("modules.twin.changes.assessed")
                  : t("modules.twin.changes.assessedRoutine"),
              });
            } catch (err) {
              toast.push({ tone: "warning", message: refusalMessage(t, err) });
            }
          }}
        >
          <label className="flex flex-col gap-1 text-xs font-medium text-slate-700">
            {t("modules.twin.changes.assessmentLabel")}
            <textarea
              data-testid="change-assessment"
              value={assessment}
              onChange={(e) => setAssessment(e.target.value)}
              rows={3}
              className="rounded border border-paper-2 px-2 py-1 text-sm font-normal"
            />
          </label>
          <div>
            <button
              type="submit"
              data-testid="change-assess"
              disabled={!assessment.trim() || assess.isPending}
              className={PRIMARY}
            >
              {t("modules.twin.changes.assess")}
            </button>
          </div>
        </form>
      )}
      {!isOpen && (
        <div data-testid="change-assessment-record" className="text-xs text-slate-700">
          <p>
            {t("modules.twin.changes.assessedBy", {
              who: change.assessed_by_name ?? t("modules.twin.changes.someone"),
              date: change.assessed_at ? formatDate(change.assessed_at, lang) : "",
            })}
          </p>
          <p className="mt-0.5 whitespace-pre-wrap" dir="auto">
            {change.assessment}
          </p>
          {change.reached_ibs_count != null && (
            <p className="mt-0.5" data-testid="change-reached">
              {t("modules.twin.changes.reached", { count: change.reached_ibs_count })}
            </p>
          )}
          {(change.reached ?? []).length > 0 && (
            <p className="mt-0.5" data-testid="change-reached-list">
              {t("modules.twin.changes.reachedServices")}{" "}
              {(change.reached ?? []).map((r, i) => (
                <span key={r.ibs_service_id}>
                  {i > 0 && ", "}
                  <Link
                    to={`/twin/ibs/${r.ibs_service_id}`}
                    className="text-brand-brassDark hover:underline"
                  >
                    <bdi>{r.name}</bdi>
                  </Link>
                </span>
              ))}
            </p>
          )}
          {change.status === "closed" && change.closed_at && (
            <p className="mt-0.5" data-testid="change-closed-by">
              {change.closed_by_name
                ? t("modules.twin.changes.closedBy", {
                    who: change.closed_by_name,
                    date: formatDate(change.closed_at, lang),
                  })
                : t("modules.twin.changes.closedOn", {
                    date: formatDate(change.closed_at, lang),
                  })}
            </p>
          )}
        </div>
      )}
      {change.status === "assessed" && may && !confirmingClose && (
        <div>
          <button
            type="button"
            data-testid="change-close"
            className={BTN}
            onClick={() => setConfirmingClose(true)}
          >
            {t("modules.twin.changes.close")}
          </button>
        </div>
      )}
      {/* Round 19 G39 -- Close was one press and cannot be undone: ask
          first, in the page, not in a browser dialog. */}
      {change.status === "assessed" && may && confirmingClose && (
        <div
          data-testid="change-close-confirm"
          className="flex flex-wrap items-center gap-2 text-xs text-slate-700"
        >
          <span>{t("modules.twin.changes.closeConfirm")}</span>{" "}
          <button
            type="button"
            data-testid="change-close-yes"
            className={PRIMARY}
            disabled={close.isPending}
            onClick={async () => {
              try {
                await close.mutateAsync();
                setConfirmingClose(false);
              } catch (err) {
                toast.push({ tone: "warning", message: refusalMessage(t, err) });
              }
            }}
          >
            {t("modules.twin.changes.closeConfirmYes")}
          </button>{" "}
          <button
            type="button"
            data-testid="change-close-no"
            className="text-slate-600 hover:underline"
            onClick={() => setConfirmingClose(false)}
          >
            {t("modules.twin.changes.closeConfirmNo")}
          </button>
        </div>
      )}
    </div>
  );
}
